import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from 'component/Layout/Form/Checkbox';

import classNames from 'classnames';
import {
    castPopularFilterValue,
    getPopularFilterConfig,
} from 'config/SearchForm/popularFilter';

import { useMeta } from 'hooks/form/meta';
import { useChangedFilterList } from 'hooks/form/useChangedFilterList';
import { useSearchAtom } from 'hooks/form/useSearchAtom';
import { QaIdentifier } from 'hooks/useQa';

import { currentSearchAtom } from 'data/Atom/Search';

import { PopularFilterInterface } from 'types/Search/popularFilter';

import { FilterKey } from 'proto/Search/PopularFilter';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';
import { FilterProps } from '../types';

const PopularFilterList = ({
    isSticky = false,
    disableWhen = MetaValue.NoResult,
}: FilterProps) => {
    const { t } = useTranslation();
    const [filterMeta] = useMeta();
    const [currentSearch, setCurrentSearch] = useSearchAtom(currentSearchAtom);
    const { onToggle, onClear, isFilterChanged } = useChangedFilterList();
    const getDisabledStatus = useDisabledStatus(disableWhen);
    const resetPopularFilterList = useCallback(() => {
        setCurrentSearch((current) => {
            filterMeta.popularFilterList.forEach(
                <Key extends FilterKey>(
                    popularFilter: PopularFilterInterface<Key>
                ) => {
                    const filterConfig = getPopularFilterConfig(popularFilter);
                    const filterValue = castPopularFilterValue(popularFilter);
                    const isSelected = filterConfig.stateProvider(
                        current,
                        filterValue
                    );
                    if (isSelected) {
                        current = filterConfig.stateUpdate(
                            current,
                            filterValue
                        );
                        onClear(() => [filterValue]);
                    }
                }
            );

            return current;
        });
    }, [setCurrentSearch, filterMeta.popularFilterList, onClear]);

    return (
        <Segment
            headline={{ text: t('TRANS-frequently-used-filter') }}
            withDeleteLink
            overflowThreshold={
                isSticky ? filterMeta.popularFilterList.length : 6
            }
            qaIdentifier={QaIdentifier.Search_Filter_Section_Popular}
            onDelete={() => resetPopularFilterList()}
            filterList={filterMeta.popularFilterList.map(
                <Key extends FilterKey>(
                    popularFilter: PopularFilterInterface<Key>
                ) => {
                    const filterConfig = getPopularFilterConfig(popularFilter);
                    const filterValue = castPopularFilterValue(popularFilter);
                    const meta = filterConfig.metaProvider(
                        filterMeta,
                        filterValue
                    );
                    const isSelected = filterConfig.stateProvider(
                        currentSearch,
                        filterValue
                    );
                    const label = filterConfig.labelProvider(
                        filterValue,
                        t,
                        filterMeta
                    );
                    const id = `${popularFilter.filterKey}_${popularFilter.value}`;

                    const tooltip = filterConfig.tooltipProvider?.(
                        filterValue,
                        t
                    );

                    return {
                        content: (
                            <Checkbox
                                key={id}
                                input={{
                                    id: `cbPopularFilter${id}`,
                                    value: filterValue,
                                    name: `cbPopularFilter${filterValue}`,
                                }}
                                isDisabled={
                                    !isSelected &&
                                    meta &&
                                    getDisabledStatus(meta)
                                }
                                isOnlyDeselectable={
                                    meta?.hasCount &&
                                    meta.count === 0 &&
                                    isSelected
                                }
                                onChange={() => {
                                    onToggle(filterValue);
                                    setCurrentSearch((current) =>
                                        filterConfig.stateUpdate(
                                            current,
                                            filterValue
                                        )
                                    );
                                }}
                                isChecked={isSelected}
                                withUXSelection
                                customClass={{
                                    Wrapper: classNames({
                                        [styles.Changed]:
                                            isFilterChanged(filterValue),
                                    }),
                                }}
                            >
                                <FilterLabel
                                    labelText={label}
                                    tooltip={tooltip}
                                    {...meta}
                                />
                            </Checkbox>
                        ),
                        isSelected,
                        filterValue: label,
                    };
                }
            )}
        />
    );
};

export default PopularFilterList;
